// src/pages/ValmikiPartner.jsx
// VALMIKI AI LAB — Partner With Us page (Page 5 of the 6-page Phase 1 architecture)
// ONE self-contained file: Navbar + Footer + all CSS live here (same design system as the other pages).
//
// ANIMATION IDEA — a partnership is two sides coming together, so that is the motion:
//   • Hero            : A College dot travels to VALMIKI, then to AI-ready talent, then to Placement.
//                       Desktop: left → right pipeline. Mobile: top → bottom vertical pipeline.
//                       Both "AI-ready talent" and "Placement" are highlighted as the outcome pair.
//   • For colleges    : the "College provides" and "VALMIKI provides" columns slide in from opposite sides and
//                       join at a gold "+" when the section scrolls into view.
//   • Journey         : a gold line walks through the six implementation steps, one after another.
//   • Industry        : the five ways to partner are marked off one by one.
// All motion is turned off for prefers-reduced-motion (the final state is shown instead).
// Lead popup: <ValmikiLeadForm /> opens itself after its own timer (see that file).

import React, { useEffect, useRef, useState } from "react";
import ValmikiFooter from "./ValmikiFooter";
import ValmikiLeadForm from "./ValmikiLeadPopup";

/* ───────────────────────── CONTENT ───────────────────────── */

const collegeProvides = [
  "Academic infrastructure",
  "Student access",
  "Campus coordination",
  "Timetable support",
  "Institutional support",
];

const valmikiProvides = [
  "Industry-oriented program architecture",
  "Specialist faculty network",
  "Practical learning and projects",
  "Academic operations framework",
  "Internship and career-readiness ecosystem",
];

const benefits = [
  "Specialist AI learning",
  "Applied curriculum and projects",
  "Faculty support",
  "Portfolio-based learning",
  "Internship framework",
  "Career readiness and placement assistance framework",
];

const journey = [
  "Discovery meeting",
  "Requirement mapping",
  "Program proposal",
  "Partnership agreement",
  "Academic planning",
  "Batch launch",
];

const industryWays = [
  "Internship partnerships",
  "Industry mentorship",
  "Student project challenges",
  "Talent pipeline engagement",
  "Entry-level hiring opportunities",
];

/* ───────────────────────── STYLES (in-page) ───────────────────────── */

const css = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400&display=swap');

.vl-root{
  --ink:#0B1626; --ink-2:#132238; --ink-3:#08111E;
  --paper:#F3F5F8; --white:#FFFFFF;
  --gold:#C8A24A; --gold-hi:#E0BC62; --gold-deep:#7D5F12;
  --slate:#4F5C73; --on-ink:#F3F5F8; --on-ink-mute:rgba(243,245,248,.72);
  --line:rgba(11,22,38,.14); --line-ink:rgba(243,245,248,.16);
  --serif:'Newsreader',Georgia,'Times New Roman',serif;
  --sans:'IBM Plex Sans','Segoe UI',Arial,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,Menlo,Consolas,monospace;
  font-family:var(--sans); color:var(--ink); background:var(--paper);
  line-height:1.6; -webkit-font-smoothing:antialiased; margin:0;
}
.vl-root *{box-sizing:border-box;}
:where(.vl-root) :where(h1,h2,h3,p,ul,ol){margin:0;padding:0;}
:where(.vl-root) :where(ul,ol){list-style:none;}
:where(.vl-root) a{color:inherit;}
.vl-root :focus-visible{outline:2px solid var(--gold-hi);outline-offset:3px;}

.vl-wrap{max-width:1120px;margin:0 auto;padding:0 24px;}
.vl-section{padding:104px 0;}
.vl-dark{background:var(--ink);color:var(--on-ink);}
.vl-white{background:var(--white);}

.vl-h2{font-family:var(--serif);font-weight:500;font-size:clamp(2rem,4vw,3rem);line-height:1.12;letter-spacing:-.015em;}
.vl-h3{font-family:var(--serif);font-weight:500;font-size:1.5rem;line-height:1.2;}
.vl-lead{font-size:1.125rem;line-height:1.65;max-width:56ch;}
.vl-muted{color:var(--slate);}
.vl-dark .vl-muted{color:var(--on-ink-mute);}
.vl-statement{font-family:var(--serif);font-size:clamp(1.5rem,2.6vw,2.125rem);line-height:1.3;
  letter-spacing:-.01em;max-width:30em;margin:20px 0 0;}
.vl-head{max-width:46rem;margin-bottom:56px;}
.vl-head .vl-h2{margin-bottom:16px;}

/* buttons */
@property --vl-a{syntax:"<angle>";inherits:false;initial-value:0deg;}
.vl-actions{display:flex;flex-wrap:wrap;gap:14px;}
.vl-btn{
  --fill:#162B47; --edge:var(--gold); --line:var(--gold);
  --wipe:var(--gold); --hover-text:var(--ink); --text:var(--on-ink);
  position:relative;overflow:hidden;isolation:isolate;
  display:inline-flex;align-items:center;justify-content:center;padding:14px 28px;border-radius:2px;
  font:600 15px/1 var(--sans);text-decoration:none;cursor:pointer;color:var(--text);
  border:1px solid var(--edge);
  background-color:var(--fill);
  background-image:linear-gradient(var(--wipe),var(--wipe));
  background-repeat:no-repeat;background-origin:border-box;background-position:left center;background-size:0% 100%;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s,border-color .3s ease;
}
.vl-btn::before{
  content:"";position:absolute;inset:0;padding:2px;border-radius:inherit;pointer-events:none;
  background:conic-gradient(from var(--vl-a),
    transparent 0deg,var(--line) 55deg,transparent 56deg,
    transparent 180deg,var(--line) 235deg,transparent 236deg);
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  -webkit-mask-composite:xor;
  mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);
  mask-composite:exclude;
  animation:vl-orbit 3.2s linear infinite;
}
@keyframes vl-orbit{to{--vl-a:360deg;}}
.vl-btn:hover,.vl-btn:focus-visible{background-size:100% 100%;color:var(--hover-text);border-color:var(--wipe);}
.vl-btn-gold{animation:vl-breathe 4s ease-in-out infinite;}
@keyframes vl-breathe{
  0%,100%{background-color:#162B47;box-shadow:0 6px 14px rgba(0,0,0,.35);}
  50%{background-color:#24426B;box-shadow:0 6px 18px rgba(200,162,74,.30);}
}
.vl-btn-line-light{--fill:transparent;--edge:var(--paper);--line:var(--paper);--wipe:var(--paper);--hover-text:var(--ink);}
.vl-btn-ink{--fill:var(--ink);--edge:var(--gold);--line:var(--gold);--wipe:var(--gold);--hover-text:var(--ink);--text:var(--paper);}
.vl-btn-line-ink{--fill:transparent;--edge:var(--ink);--line:var(--ink);--wipe:var(--ink);--hover-text:var(--paper);--text:var(--ink);}
.vl-final .vl-btn-ink{--edge:var(--paper);--line:var(--paper);--wipe:var(--paper);}
@media (prefers-reduced-motion:reduce){
  .vl-btn,.vl-toggle{transition:none;}
  .vl-btn::before{animation:none;}
  .vl-btn-gold{animation:none;}
}

/* header */
.vl-header{position:sticky;top:0;z-index:50;background:var(--ink);border-bottom:1px solid var(--line-ink);}
.vl-header-row{display:flex;align-items:center;justify-content:space-between;min-height:68px;gap:24px;}
.vl-brand{display:flex;align-items:center;gap:12px;text-decoration:none;color:var(--on-ink);
  font:600 17px/1 var(--sans);letter-spacing:.14em;}
.vl-nav{display:flex;align-items:center;gap:26px;}
.vl-nav a:not(.vl-btn){font-size:12.5px;font-weight:500;letter-spacing:.09em;text-decoration:none;color:var(--on-ink-mute);
  padding:6px 0;border-bottom:1.5px solid transparent;transition:color .15s,border-color .15s;}
.vl-nav a:not(.vl-btn):hover{color:var(--on-ink);}
.vl-nav a[aria-current="page"]{color:var(--on-ink);border-bottom-color:var(--gold);}
.vl-nav .vl-btn{padding:11px 20px;font-size:12.5px;letter-spacing:.09em;}
.vl-toggle{display:none;background-color:transparent;
  background-image:linear-gradient(var(--paper),var(--paper));background-repeat:no-repeat;
  background-origin:border-box;background-position:left center;background-size:0% 100%;
  border:1px solid var(--line-ink);color:var(--on-ink);
  width:42px;height:42px;border-radius:3px;font-size:20px;cursor:pointer;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s;}
.vl-toggle:hover,.vl-toggle:focus-visible{background-size:100% 100%;color:var(--ink);}
.vl-mobile{display:none;background:var(--ink);border-top:1px solid var(--line-ink);padding:8px 24px 22px;}
.vl-mobile a:not(.vl-btn){display:block;padding:14px 0;border-bottom:1px solid var(--line-ink);text-decoration:none;
  font-size:14px;letter-spacing:.08em;color:var(--on-ink);}
.vl-mobile .vl-btn{margin-top:18px;width:100%;}
@media (max-width:1000px){
  .vl-nav{display:none;}
  .vl-toggle{display:block;}
  .vl-mobile.open{display:block;}
}

/* ───────── hero ───────── */
.vl-hero{position:relative;overflow:hidden;background-color:var(--ink);color:var(--on-ink);padding:88px 0 104px;
  background-image:linear-gradient(rgba(243,245,248,.035) 1px,transparent 1px),
                   linear-gradient(90deg,rgba(243,245,248,.035) 1px,transparent 1px);
  background-size:56px 56px;}
.vl-hero-grid{position:relative;z-index:1;display:grid;grid-template-columns:1.1fr .9fr;gap:64px;align-items:center;}
.vl-h1{font-family:var(--serif);font-weight:500;font-size:clamp(2.4rem,5vw,4rem);line-height:1.05;
  letter-spacing:-.025em;margin-bottom:24px;}
.vl-hero .vl-lead{color:var(--on-ink-mute);margin-bottom:36px;}
.vl-hero-fx{position:absolute;inset:0;pointer-events:none;overflow:hidden;}
.vl-hero-glow{position:absolute;right:-6%;top:6%;width:560px;height:560px;border-radius:50%;
  background:radial-gradient(circle,rgba(200,162,74,.18),transparent 65%);}

/* hero panel: the pipeline College → VALMIKI → AI-ready talent → Placement */
.vl-pair{background:var(--ink-2);border:1px solid var(--line-ink);border-radius:10px;padding:22px 24px 16px;
  box-shadow:0 24px 60px rgba(0,0,0,.35);}
.vl-pair-head{display:flex;justify-content:space-between;align-items:baseline;gap:12px;
  padding-bottom:14px;border-bottom:1px solid var(--line-ink);}
.vl-pair-title{font:600 15px/1.3 var(--sans);}
.vl-pair-tag{font:400 12px/1 var(--mono);color:var(--on-ink-mute);}
.vl-pair svg{display:block;width:100%;height:auto;max-width:540px;margin:10px auto 0;}

/* the vertical variant of the panel is hidden on desktop, shown on mobile */
.vl-pair-v{display:none;}

.vl-pn{fill:var(--ink-2);stroke:var(--gold);stroke-width:1.6;}
.vl-pn-core{fill:var(--gold);stroke:var(--gold-hi);stroke-width:1.6;}
.vl-pn-halo{fill:none;stroke:var(--gold);stroke-width:1.2;opacity:0;}
/* outer emphasis ring used on the two highlighted outcome nodes (AI-ready talent + Placement) */
.vl-pn-ring{fill:none;stroke:var(--gold-hi);stroke-width:1;opacity:.55;}
.vl-pl{stroke:var(--line-ink);stroke-width:1.5;stroke-dasharray:3 6;}
.vl-pt{font-family:var(--serif);font-size:18px;fill:var(--on-ink);}
.vl-pt-hi{font-family:var(--serif);font-size:18px;fill:var(--gold-hi);font-weight:600;}
.vl-pt-s{font:400 12px var(--mono);fill:var(--on-ink-mute);}
.vl-pt-s-hi{font:400 12px var(--mono);fill:var(--gold-hi);}
.vl-pdot{fill:var(--gold-hi);}
.vl-pdot-1,.vl-pdot-2,.vl-pdot-3{transform-box:view-box;}
.vl-vpdot{fill:var(--gold-hi);}
.vl-vpdot-1,.vl-vpdot-2,.vl-vpdot-3{transform-box:view-box;}
.vl-pinner{fill:none;stroke:var(--gold);stroke-width:1.4;}
.vl-pinner-hi{fill:none;stroke:var(--gold-hi);stroke-width:1.6;}

@media (prefers-reduced-motion:no-preference){
  .vl-hero{animation:vl-grid 10s linear infinite;}
  .vl-hero-glow{animation:vl-glow 4s ease-in-out infinite alternate;}
  .vl-h1,.vl-hero .vl-lead,.vl-hero .vl-actions{animation:vl-rise .5s cubic-bezier(.2,.7,.2,1) both;}
  .vl-h1{animation-delay:.05s;}
  .vl-hero .vl-lead{animation-delay:.2s;}
  .vl-hero .vl-actions{animation-delay:.35s;}
  .vl-pair{animation:vl-panel-in .55s cubic-bezier(.2,.7,.2,1) .1s both;}
  .vl-hero .vl-btn::before{animation-duration:2s;}
  .vl-hero .vl-btn-gold{animation-duration:2.5s;}
  /* Horizontal pipeline: dot 1 (College → VALMIKI), dot 2 (VALMIKI → AI talent), dot 3 (AI talent → Placement) */
  .vl-pdot-1{animation:vl-seq-1 6s ease-in-out 1s infinite;}
  .vl-pdot-2{animation:vl-seq-2 6s ease-in-out 1s infinite;}
  .vl-pdot-3{animation:vl-seq-3 6s ease-in-out 1s infinite;}
  /* Vertical pipeline: same sequence, travelling downward */
  .vl-vpdot-1{animation:vl-vseq-1 6s ease-in-out 1s infinite;}
  .vl-vpdot-2{animation:vl-vseq-2 6s ease-in-out 1s infinite;}
  .vl-vpdot-3{animation:vl-vseq-3 6s ease-in-out 1s infinite;}
  /* Two halos: one pulses when AI-ready talent is reached, one pulses at Placement (the outcome) */
  .vl-pn-halo{animation:vl-halo 6s ease-out 1s infinite;}
  .vl-pn-halo-2{animation:vl-halo-2 6s ease-out 1s infinite;}
  /* The two outcome rings breathe gently to signal the highlighted results */
  .vl-pn-ring{animation:vl-ring 4s ease-in-out infinite;}
  .vl-pn-ring-2{animation:vl-ring 4s ease-in-out infinite .6s;}
}
@keyframes vl-grid{to{background-position:56px 56px,56px 56px;}}
@keyframes vl-glow{to{transform:translate(-50px,40px) scale(1.12);}}
@keyframes vl-rise{from{opacity:0;transform:translateY(22px);}to{opacity:1;transform:none;}}
@keyframes vl-panel-in{from{opacity:0;transform:translateX(28px);}to{opacity:1;transform:none;}}

/* sequential travel, horizontal: each dot moves 90px (edge of one node to edge of the next) */
@keyframes vl-seq-1{
  0%{transform:translateX(0);opacity:0;}
  4%{opacity:1;}
  26%{transform:translateX(90px);opacity:1;}
  30%,100%{transform:translateX(90px);opacity:0;}
}
@keyframes vl-seq-2{
  0%,30%{transform:translateX(0);opacity:0;}
  34%{opacity:1;}
  56%{transform:translateX(90px);opacity:1;}
  60%,100%{transform:translateX(90px);opacity:0;}
}
@keyframes vl-seq-3{
  0%,60%{transform:translateX(0);opacity:0;}
  64%{opacity:1;}
  86%{transform:translateX(90px);opacity:1;}
  90%,100%{transform:translateX(90px);opacity:0;}
}

/* sequential travel, vertical: each dot moves 68px downward (edge to edge) */
@keyframes vl-vseq-1{
  0%{transform:translateY(0);opacity:0;}
  4%{opacity:1;}
  26%{transform:translateY(68px);opacity:1;}
  30%,100%{transform:translateY(68px);opacity:0;}
}
@keyframes vl-vseq-2{
  0%,30%{transform:translateY(0);opacity:0;}
  34%{opacity:1;}
  56%{transform:translateY(68px);opacity:1;}
  60%,100%{transform:translateY(68px);opacity:0;}
}
@keyframes vl-vseq-3{
  0%,60%{transform:translateY(0);opacity:0;}
  64%{opacity:1;}
  86%{transform:translateY(68px);opacity:1;}
  90%,100%{transform:translateY(68px);opacity:0;}
}

/* halo around "AI-ready talent" — pulses mid-pipeline */
@keyframes vl-halo{
  0%,58%{opacity:0;transform:scale(1);}
  66%{opacity:.85;transform:scale(1);}
  92%,100%{opacity:0;transform:scale(1.5);}
}
/* halo around "Placement" — pulses at the end of the pipeline, stronger than the mid one */
@keyframes vl-halo-2{
  0%,80%{opacity:0;transform:scale(1);}
  86%{opacity:1;transform:scale(1);}
  100%{opacity:0;transform:scale(1.65);}
}
/* gentle breathing ring for the two highlighted outcome nodes */
@keyframes vl-ring{
  0%,100%{opacity:.45;}
  50%{opacity:.85;}
}

/* ───────── for colleges: two sides join ───────── */
.vl-join{position:relative;display:grid;grid-template-columns:1fr 1fr;gap:72px;margin-top:56px;}
.vl-side-title{font:600 14px/1.4 var(--sans);color:var(--gold-deep);margin-bottom:6px;}
.vl-side-list li{padding:18px 0;border-bottom:1px solid var(--line);font-family:var(--serif);font-size:1.3rem;line-height:1.3;}
.vl-side-list li:first-child{border-top:1px solid var(--line);}
.vl-side-b .vl-side-title{color:var(--ink);}
.vl-side-b .vl-side-list li:first-child{border-top-color:var(--ink);}
.vl-plus{position:absolute;left:50%;top:0;width:44px;height:44px;margin:-2px 0 0 -22px;border-radius:50%;
  background:var(--gold);color:var(--ink);display:flex;align-items:center;justify-content:center;
  font:500 26px/1 var(--sans);z-index:1;}
@media (prefers-reduced-motion:no-preference){
  .vl-side-a,.vl-side-b,.vl-plus{transition:transform .8s cubic-bezier(.2,.7,.2,1),opacity .8s ease;}
  .vl-plus{transition-delay:.7s;}
  .vl-join:not(.in) .vl-side-a{transform:translateX(-44px);opacity:0;}
  .vl-join:not(.in) .vl-side-b{transform:translateX(44px);opacity:0;}
  .vl-join:not(.in) .vl-plus{transform:scale(0);opacity:0;}
}
.vl-cta-row{margin-top:56px;}

/* ───────── partnership benefits ───────── */
.vl-benefits{display:grid;grid-template-columns:1fr 1fr;border-top:1px solid var(--line-ink);}
.vl-benefits li{display:flex;align-items:center;gap:16px;padding:22px 24px 22px 0;border-bottom:1px solid var(--line-ink);
  font-family:var(--serif);font-size:1.35rem;line-height:1.3;}
.vl-benefits li:nth-child(even){padding-left:32px;border-left:1px solid var(--line-ink);}
.vl-tick{flex:none;width:22px;height:22px;border-radius:50%;border:1.5px solid var(--gold);
  display:flex;align-items:center;justify-content:center;}
@media (prefers-reduced-motion:no-preference){
  .vl-benefits li{transition:opacity .5s ease calc(var(--i) * .3s + .1s),transform .5s cubic-bezier(.2,.7,.2,1) calc(var(--i) * .3s + .1s);}
  .vl-benefits .vl-tick path{stroke-dasharray:20;transition:stroke-dashoffset .4s ease calc(var(--i) * .3s + .35s);}
  .vl-benefits:not(.in) li{opacity:0;transform:translateY(8px);}
  .vl-benefits:not(.in) .vl-tick path{stroke-dashoffset:20;}
}

/* ───────── implementation journey (a gold line walks through the six steps) ───────── */
.vl-steps{display:grid;grid-template-columns:repeat(6,1fr);}
.vl-steps li{position:relative;padding:28px 14px 0 0;border-top:1.5px solid var(--line);}
.vl-steps li::after{content:"";position:absolute;top:-1.5px;left:0;right:0;height:1.5px;background:var(--gold);transform-origin:left;}
.vl-steps li::before{content:"";position:absolute;top:-6px;left:0;width:10px;height:10px;border-radius:50%;background:var(--gold);z-index:1;}
.vl-steps li:last-child::after{right:auto;width:0;}
.vl-step-n{display:block;font:400 12.5px/1 var(--mono);color:var(--slate);margin-bottom:10px;}
.vl-step-t{font-family:var(--serif);font-size:1.3rem;line-height:1.2;}
.vl-steps li:last-child .vl-step-t{color:var(--gold-deep);font-weight:600;}
@media (prefers-reduced-motion:no-preference){
  .vl-steps li::after{transition:transform .4s cubic-bezier(.5,0,.3,1) calc(var(--i) * .45s + .15s);}
  .vl-steps li::before{transition:transform .3s ease calc(var(--i) * .45s);}
  .vl-step-n,.vl-step-t{transition:opacity .4s ease calc(var(--i) * .45s + .1s);}
  .vl-steps:not(.in) li::after{transform:scaleX(0);}
  .vl-steps:not(.in) li::before{transform:scale(0);}
  .vl-steps:not(.in) .vl-step-n,.vl-steps:not(.in) .vl-step-t{opacity:.25;}
}

/* ───────── industry & corporate ───────── */
.vl-split{display:grid;grid-template-columns:1fr 1fr;gap:72px;align-items:start;}
.vl-split .vl-h2{margin-bottom:24px;}
.vl-ways li{position:relative;padding:22px 0;border-top:1px solid var(--line-ink);
  font-family:var(--serif);font-size:1.5rem;line-height:1.3;}
.vl-ways li:last-child{border-bottom:1px solid var(--line-ink);}
.vl-ways li::after{content:"";position:absolute;top:-1px;left:0;right:0;height:2px;background:var(--gold);transform-origin:left;}
@media (prefers-reduced-motion:no-preference){
  .vl-ways li::after{transition:transform .6s cubic-bezier(.5,0,.3,1) calc(var(--i) * .4s + .1s);}
  .vl-ways li{transition:opacity .5s ease calc(var(--i) * .4s + .1s);}
  .vl-ways:not(.in) li::after{transform:scaleX(0);}
  .vl-ways:not(.in) li{opacity:.3;}
}

/* ───────── final cta ───────── */
.vl-final{background:var(--gold);color:var(--ink);padding:96px 0;}
.vl-final .vl-h2{font-size:clamp(2.1rem,4.6vw,3.6rem);max-width:19em;margin-bottom:40px;}

/* footer */
.vl-footer{background:var(--ink-3);color:var(--on-ink);padding:56px 0 40px;}
.vl-footer-row{display:flex;flex-wrap:wrap;justify-content:space-between;gap:32px;padding-bottom:36px;border-bottom:1px solid var(--line-ink);}
.vl-footer p{font-size:14px;color:var(--on-ink-mute);max-width:38ch;margin-top:14px;}
.vl-footer nav{display:flex;flex-wrap:wrap;gap:12px 28px;align-items:flex-start;}
.vl-footer nav a{font-size:13px;letter-spacing:.08em;text-decoration:none;color:var(--on-ink-mute);}
.vl-footer nav a:hover{color:var(--on-ink);}
.vl-footer .vl-copy{padding-top:24px;margin-top:0;max-width:none;font-size:13px;color:var(--on-ink-mute);}

/* responsive */
@media (max-width:1000px){
  .vl-steps{grid-template-columns:repeat(3,1fr);row-gap:36px;}
}
@media (max-width:860px){
  .vl-section{padding:72px 0;}
  .vl-hero{padding:56px 0 72px;}
  .vl-hero-grid,.vl-split,.vl-join{grid-template-columns:1fr;gap:48px;}
  .vl-plus{display:none;}
  .vl-benefits{grid-template-columns:1fr;}
  .vl-benefits li,.vl-benefits li:nth-child(even){padding:20px 0;border-left:0;}
}
@media (max-width:560px){
  .vl-wrap{padding:0 20px;}
  .vl-actions .vl-btn{width:100%;}
  .vl-steps{grid-template-columns:repeat(2,1fr);}
}

/* ───────── mobile: swap the hero pipeline to a full-width VERTICAL panel ───────── */
@media (max-width:860px){
  .vl-pair-h{display:none;}                 /* hide the horizontal pipeline */
  .vl-pair-v{
    display:block;                          /* show the vertical pipeline */
    margin:0 -24px;                         /* break out of the 24px page padding → full width */
    padding:24px 20px 22px;
    border-radius:0;
    border-left:0;
    border-right:0;
    box-shadow:0 20px 44px rgba(0,0,0,.42);
  }
  .vl-pair-v .vl-pair-head{
    flex-direction:column;
    align-items:flex-start;
    gap:6px;
    padding-bottom:16px;
  }
  .vl-pair-v .vl-pair-title{font-size:16px;}
  .vl-pair-v .vl-pair-tag{font-size:11.5px;}
  .vl-pair-v svg{
    display:block;
    width:100%;
    height:auto;
    max-width:380px;
    margin:18px auto 0;
  }
}

@media (max-width:560px){
  .vl-pair-v{
    margin:0 -20px;                         /* matches the 20px page padding at this width */
    padding:22px 14px 20px;
  }
  .vl-pair-v svg{max-width:340px;}
}
`;

/* ───────────────────────── HELPERS ───────────────────────── */

// Becomes true the first time the element scrolls into view (so scroll animations play once).
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
}

const Tick = () => (
  <span className="vl-tick" aria-hidden="true">
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2.5 6.4L5 8.9L9.6 3.6" stroke="#E0BC62" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

/* Hero graphic (DESKTOP) — a 4-stage pipeline read left → right:
   1. College          → provides campus + students
   2. VALMIKI          → provides program + faculty
   3. AI-ready talent  → built together (highlighted outcome)
   4. Placement        → career readiness (highlighted outcome, the end goal)
   A gold dot travels left → right, one stage at a time, on a 6s loop.
   Both outcome nodes (3 and 4) are gold-filled, ringed, and haloed. */
function PartnerMeet() {
  return (
    <svg
      viewBox="0 0 600 190"
      role="img"
      aria-label="College partners with VALMIKI to build AI-ready talent, leading to placement"
    >
      {/* Dashed connection lines between each stage */}
      <line className="vl-pl" x1="105" y1="70" x2="195" y2="70" />
      <line className="vl-pl" x1="255" y1="70" x2="345" y2="70" />
      <line className="vl-pl" x1="405" y1="70" x2="495" y2="70" />

      {/* Halos around the two highlighted outcome nodes */}
      <circle
        className="vl-pn-halo"
        cx="375"
        cy="70"
        r="34"
        style={{ transformOrigin: "375px 70px", transformBox: "view-box" }}
      />
      <circle
        className="vl-pn-halo vl-pn-halo-2"
        cx="525"
        cy="70"
        r="36"
        style={{ transformOrigin: "525px 70px", transformBox: "view-box" }}
      />

      {/* Outer emphasis rings on the two highlighted outcome nodes */}
      <circle className="vl-pn-ring" cx="375" cy="70" r="38" />
      <circle className="vl-pn-ring vl-pn-ring-2" cx="525" cy="70" r="41" />

      {/* Stage nodes */}
      <circle className="vl-pn" cx="75" cy="70" r="30" />
      <circle className="vl-pn" cx="225" cy="70" r="30" />
      {/* Highlighted outcome 1: AI-ready talent */}
      <circle className="vl-pn-core" cx="375" cy="70" r="30" />
      {/* Highlighted outcome 2: Placement — slightly larger to signal it is the end goal */}
      <circle className="vl-pn-core" cx="525" cy="70" r="33" />

      {/* Inner dots */}
      <circle className="vl-pinner" cx="75" cy="70" r="6" />
      <circle className="vl-pinner" cx="225" cy="70" r="6" />
      <circle className="vl-pinner-hi" cx="375" cy="70" r="6" />
      <circle className="vl-pinner-hi" cx="525" cy="70" r="6" />

      {/* Travelling dots */}
      <circle className="vl-pdot vl-pdot-1" cx="105" cy="70" r="5" />
      <circle className="vl-pdot vl-pdot-2" cx="255" cy="70" r="5" />
      <circle className="vl-pdot vl-pdot-3" cx="405" cy="70" r="5" />

      {/* Labels — the two outcome labels use the highlighted style */}
      <text className="vl-pt" x="75" y="132" textAnchor="middle">College</text>
      <text className="vl-pt-s" x="75" y="152" textAnchor="middle">campus + students</text>

      <text className="vl-pt" x="225" y="132" textAnchor="middle">VALMIKI</text>
      <text className="vl-pt-s" x="225" y="152" textAnchor="middle">program + faculty</text>

      <text className="vl-pt-hi" x="375" y="132" textAnchor="middle">AI-ready talent</text>
      <text className="vl-pt-s-hi" x="375" y="152" textAnchor="middle">built together</text>

      <text className="vl-pt-hi" x="525" y="132" textAnchor="middle">Placement</text>
      <text className="vl-pt-s-hi" x="525" y="152" textAnchor="middle">career readiness</text>
    </svg>
  );
}

/* Hero graphic (MOBILE) — the SAME 4-stage pipeline, but read top → bottom.
   Nodes sit in a single column on the left, labels sit beside them on the right,
   so the whole diagram fits a phone screen and reads naturally as a downward flow.
   A gold dot travels downward, one stage at a time, on a 6s loop. */
function PartnerMeetVertical() {
  return (
    <svg
      viewBox="0 0 320 500"
      role="img"
      aria-label="College partners with VALMIKI to build AI-ready talent, leading to placement"
    >
      {/* Vertical dashed connection lines between each stage */}
      <line className="vl-pl" x1="64" y1="80" x2="64" y2="148" />
      <line className="vl-pl" x1="64" y1="204" x2="64" y2="272" />
      <line className="vl-pl" x1="64" y1="328" x2="64" y2="396" />

      {/* Halos around the two highlighted outcome nodes */}
      <circle
        className="vl-pn-halo"
        cx="64"
        cy="300"
        r="32"
        style={{ transformOrigin: "64px 300px", transformBox: "view-box" }}
      />
      <circle
        className="vl-pn-halo vl-pn-halo-2"
        cx="64"
        cy="424"
        r="34"
        style={{ transformOrigin: "64px 424px", transformBox: "view-box" }}
      />

      {/* Outer emphasis rings on the two highlighted outcome nodes */}
      <circle className="vl-pn-ring" cx="64" cy="300" r="35" />
      <circle className="vl-pn-ring vl-pn-ring-2" cx="64" cy="424" r="38" />

      {/* Stage nodes */}
      <circle className="vl-pn" cx="64" cy="52" r="28" />
      <circle className="vl-pn" cx="64" cy="176" r="28" />
      {/* Highlighted outcome 1: AI-ready talent */}
      <circle className="vl-pn-core" cx="64" cy="300" r="28" />
      {/* Highlighted outcome 2: Placement — slightly larger to signal it is the end goal */}
      <circle className="vl-pn-core" cx="64" cy="424" r="30" />

      {/* Inner dots */}
      <circle className="vl-pinner" cx="64" cy="52" r="6" />
      <circle className="vl-pinner" cx="64" cy="176" r="6" />
      <circle className="vl-pinner-hi" cx="64" cy="300" r="6" />
      <circle className="vl-pinner-hi" cx="64" cy="424" r="6" />

      {/* Travelling dots (move downward, edge to edge) */}
      <circle className="vl-vpdot vl-vpdot-1" cx="64" cy="80" r="5" />
      <circle className="vl-vpdot vl-vpdot-2" cx="64" cy="204" r="5" />
      <circle className="vl-vpdot vl-vpdot-3" cx="64" cy="328" r="5" />

      {/* Labels — placed beside each node so the column stays narrow and readable */}
      <text className="vl-pt" x="112" y="50" textAnchor="start">College</text>
      <text className="vl-pt-s" x="112" y="70" textAnchor="start">campus + students</text>

      <text className="vl-pt" x="112" y="174" textAnchor="start">VALMIKI</text>
      <text className="vl-pt-s" x="112" y="194" textAnchor="start">program + faculty</text>

      <text className="vl-pt-hi" x="112" y="298" textAnchor="start">AI-ready talent</text>
      <text className="vl-pt-s-hi" x="112" y="318" textAnchor="start">built together</text>

      <text className="vl-pt-hi" x="112" y="422" textAnchor="start">Placement</text>
      <text className="vl-pt-s-hi" x="112" y="442" textAnchor="start">career readiness</text>
    </svg>
  );
}

/* ───────────────────── NAVBAR + FOOTER (inline) ───────────────────── */

const VALMIKI_ROUTES = {
  home: "/valmiki",
  about: "/valmiki/about",
  program: "/valmiki/program",
  lab: "/valmiki/lab",
  partner: "/valmiki/partner",
  contact: "/valmiki/contact",
};

// The two partnership CTAs go to the contact page with a `type` so the form can be pre-selected.
const CTA_COLLEGE = `${VALMIKI_ROUTES.contact}?type=college`;
const CTA_INDUSTRY = `${VALMIKI_ROUTES.contact}?type=industry`;

// Partner With Us is a button in the nav, so no text link is marked current (the button is the active page).
const VALMIKI_NAV_LINKS = [
  { label: "HOME", href: VALMIKI_ROUTES.home },
  { label: "ABOUT VALMIKI", href: VALMIKI_ROUTES.about },
  { label: "PROGRAM", href: VALMIKI_ROUTES.program },
  { label: "VALMIKI AI LAB", href: VALMIKI_ROUTES.lab },
  { label: "CONTACT", href: VALMIKI_ROUTES.contact },
];

const ValmikiBrandMark = () => (
  <svg width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
    <circle cx="15" cy="15" r="14" stroke="#C8A24A" strokeWidth="1.2" />
    <path d="M9 19L15 8L21 19" stroke="#F3F5F8" strokeWidth="1.4" strokeLinejoin="round" />
    <circle cx="15" cy="15.5" r="1.6" fill="#C8A24A" />
  </svg>
);

/* ───────────────────────── PAGE ───────────────────────── */

export default function ValmikiPartner() {
  const [joinRef, joinSeen] = useInView(0.3);
  const [benefitsRef, benefitsSeen] = useInView(0.3);
  const [stepsRef, stepsSeen] = useInView(0.4);
  const [waysRef, waysSeen] = useInView(0.35);

  return (
    <div className="vl-root" id="top">
      <style>{css}</style>

      <main>
        {/* HERO */}
        <section className="vl-hero" aria-labelledby="vl-partner-title">
          <div className="vl-hero-fx" aria-hidden="true"><span className="vl-hero-glow" /></div>
          <div className="vl-wrap vl-hero-grid">
            <div>
              <h1 className="vl-h1" id="vl-partner-title">Build the Next Generation of AI-Ready Talent.</h1>
              <p className="vl-lead">
                Partner with VALMIKI AI LAB to strengthen practical AI learning, student capability and career readiness.
              </p>
              <div className="vl-actions">
                <a className="vl-btn vl-btn-gold" href="#vl-colleges">For Colleges</a>
                <a className="vl-btn vl-btn-line-light" href="#vl-industry">For Industry &amp; Corporate Partners</a>
              </div>
            </div>

            {/* Horizontal pipeline — desktop */}
            <aside className="vl-pair vl-pair-h" aria-label="How a college and VALMIKI partner">
              <div className="vl-pair-head">
                <span className="vl-pair-title">Two sides, one program</span>
                <span className="vl-pair-tag">college + VALMIKI</span>
              </div>
              <PartnerMeet />
            </aside>

            {/* Vertical pipeline — mobile (CSS swaps which one is visible) */}
            <aside className="vl-pair vl-pair-v" aria-label="How a college and VALMIKI partner">
              <div className="vl-pair-head">
                <span className="vl-pair-title">Two sides, one program</span>
                <span className="vl-pair-tag">college + VALMIKI</span>
              </div>
              <PartnerMeetVertical />
            </aside>
          </div>
        </section>

        {/* FOR COLLEGES */}
        <section className="vl-section" id="vl-colleges" aria-labelledby="vl-colleges-title">
          <div className="vl-wrap">
            <h2 className="vl-h2" id="vl-colleges-title">For colleges</h2>
            <p className="vl-statement">
              Build an AI-ready learning ecosystem without creating a separate AI academic infrastructure.
            </p>

            <div ref={joinRef} className={`vl-join${joinSeen ? " in" : ""}`}>
              <div className="vl-side-a">
                <p className="vl-side-title">College provides</p>
                <ul className="vl-side-list">
                  {collegeProvides.map((c) => <li key={c}>{c}</li>)}
                </ul>
              </div>
              <span className="vl-plus" aria-hidden="true">+</span>
              <div className="vl-side-b">
                <p className="vl-side-title">VALMIKI provides</p>
                <ul className="vl-side-list">
                  {valmikiProvides.map((v) => <li key={v}>{v}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* PARTNERSHIP BENEFITS */}
        <section className="vl-section vl-dark" aria-labelledby="vl-benefits-title">
          <div className="vl-wrap">
            <div className="vl-head">
              <h2 className="vl-h2" id="vl-benefits-title">Partnership benefits</h2>
            </div>
            <ul ref={benefitsRef} className={`vl-benefits${benefitsSeen ? " in" : ""}`}>
              {benefits.map((b, i) => (
                <li key={b} style={{ "--i": i }}>
                  <Tick />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* IMPLEMENTATION JOURNEY */}
        <section className="vl-section vl-white" aria-labelledby="vl-impl-title">
          <div className="vl-wrap">
            <div className="vl-head">
              <h2 className="vl-h2" id="vl-impl-title">Implementation journey</h2>
              <p className="vl-lead vl-muted">From the first meeting to the first batch, in six steps.</p>
            </div>
            <ol ref={stepsRef} className={`vl-steps${stepsSeen ? " in" : ""}`} aria-label="Implementation steps, in order">
              {journey.map((s, i) => (
                <li key={s} style={{ "--i": i }}>
                  <span className="vl-step-n">{String(i + 1).padStart(2, "0")}</span>
                  <span className="vl-step-t">{s}</span>
                </li>
              ))}
            </ol>
            <div className="vl-actions vl-cta-row">
              <a className="vl-btn vl-btn-ink" href={CTA_COLLEGE}>Request a Partnership Meeting</a>
            </div>
          </div>
        </section>

        {/* FOR INDUSTRY & CORPORATE PARTNERS */}
        <section className="vl-section vl-dark" id="vl-industry" aria-labelledby="vl-industry-title">
          <div className="vl-wrap vl-split">
            <div>
              <h2 className="vl-h2" id="vl-industry-title">For industry &amp; corporate partners</h2>
              <p className="vl-lead vl-muted" style={{ marginBottom: 36 }}>
                Industry partners can contribute through internship opportunities, project challenges,
                mentoring, talent engagement and hiring opportunities.
              </p>
              <div className="vl-actions">
                <a className="vl-btn vl-btn-gold" href={CTA_INDUSTRY}>Start a Partnership Conversation</a>
              </div>
            </div>
            <ul ref={waysRef} className={`vl-ways${waysSeen ? " in" : ""}`}>
              {industryWays.map((w, i) => (
                <li key={w} style={{ "--i": i }}>{w}</li>
              ))}
            </ul>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="vl-final" aria-labelledby="vl-final-title">
          <div className="vl-wrap">
            <h2 className="vl-h2" id="vl-final-title">
              Let&rsquo;s build AI-ready talent together.
            </h2>
            <div className="vl-actions">
              <a className="vl-btn vl-btn-ink" href={CTA_COLLEGE}>Request a Partnership Meeting</a>
              <a className="vl-btn vl-btn-line-ink" href={CTA_INDUSTRY}>Start a Partnership Conversation</a>
            </div>
          </div>
        </section>
      </main>

      <ValmikiFooter />

      {/* Lead popup — ValmikiLeadForm opens itself after OPEN_AFTER_MS (see that file) */}
      <ValmikiLeadForm />
    </div>
  );
}